import {
  type BuiltInPolicyConditionAttribute,
  parseCustomPropertyConditionAttribute,
  POLICY_CONDITION_ATTRIBUTES,
  type PolicyConditionAttribute,
  type PolicyConditionAttributeMetadata,
} from '@provision/sidekick-utils';
import { useMemo } from 'react';
import { RuleGroupType, RuleType } from 'react-querybuilder';
import { useSelectedPartner } from 'src/graphql';
import {
  ConditionType,
  DataConditionOperator,
  FormControlType,
} from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import {
  ConditionElement,
  ConditionGroup,
  ConditionNode,
  RuleControlType,
  SelectorValue,
} from 'src/graphql/entities/behavior/behavior.types';
import {
  QueryOptionsValue,
  SupportedActionQueries,
  useOptionsQueries,
  UseQueryOptions,
} from 'src/pages/Settings/rules/use-rules-engine';
import { SUPPORT_POLICY_CONDITION_OPERATORS } from './support-policy.operators';

const operators = SUPPORT_POLICY_CONDITION_OPERATORS;

export type SupportPolicyConditionAttribute = PolicyConditionAttribute;

export type SupportPolicyConditionField = SelectorValue & {
  attribute: SupportPolicyConditionAttribute;
  group?: string;
  conditionType?: ConditionType;
};

type SupportPolicyRuleValue =
  | string
  | { value: string; label?: string }
  | { value: string; label?: string }[];

export type SupportPolicyRule = RuleType<
  string,
  DataConditionOperator,
  SupportPolicyRuleValue
> & {
  field?: SupportPolicyConditionField | string;
};

export type SupportPolicyRuleGroup = RuleGroupType<SupportPolicyRule, 'and' | 'or'> & {
  id?: string;
};

const isPrimitiveValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const toPrimitiveString = (value?: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (isPrimitiveValue(value)) {
    return String(value);
  }
  return null;
};

const toPrimitiveStringOrEmpty = (value?: unknown): string => toPrimitiveString(value) ?? '';

const toOptionsQuery = (value: SupportedActionQueries): keyof UseQueryOptions => value;

const CONDITION_FIELD_CONFIGS: Partial<
  Record<
    BuiltInPolicyConditionAttribute,
    Omit<SupportPolicyConditionField, 'attribute' | 'name' | 'value' | 'label'>
  >
> = {
  addressId: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getAddress),
    operators,
    group: 'Conversation',
    triggers: [],
  },
  clientId: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getConversationParticipantOptions),
    operators,
    group: 'Conversation',
    triggers: [],
  },
  urgency: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getEnabledConversationUrgencies),
    operators,
    group: 'Conversation',
    triggers: [],
  },
  clientToAddressId: {
    uiType: FormControlType.Text,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    operators,
    group: 'Advanced',
    triggers: [],
  },
  partnerId: {
    uiType: FormControlType.Text,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    operators,
    triggers: [],
    group: 'Advanced',
  },
  partnerGroupId: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getPartnerGroup),
    operators,
    triggers: [],
    group: 'Advanced',
  },
  siteLabelId: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getAllLabels),
    operators,
    group: 'Conversation',
    triggers: [],
  },
  serviceLevel: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getServiceLevelOptions),
    operators,
    group: 'Conversation',
    triggers: [],
  },
  clientSegmentId: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getBrand),
    operators,
    group: 'Conversation',
    triggers: [],
  },
  conversationType: {
    uiType: FormControlType.List,
    controlType: RuleControlType.ConditionControl,
    conditionType: ConditionType.Data,
    options: [],
    fieldOptions: [],
    optionsQuery: toOptionsQuery(SupportedActionQueries.getConversationType),
    operators,
    group: 'Conversation',
    triggers: [],
  },
};

export const SUPPORT_POLICY_CONDITION_FIELDS: SupportPolicyConditionField[] =
  POLICY_CONDITION_ATTRIBUTES.reduce<SupportPolicyConditionField[]>(
    (acc: SupportPolicyConditionField[], metadata: PolicyConditionAttributeMetadata) => {
      const config = CONDITION_FIELD_CONFIGS[metadata.attribute];
      if (!config) {
        return acc;
      }

      acc.push({
        ...config,
        name: metadata.attribute,
        value: metadata.attribute,
        attribute: metadata.attribute,
        label: metadata.label,
      });
      return acc;
    },
    [],
  );

const generateRuleId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID?.() ?? `rule-${Date.now()}-${Math.random()}`;
  }

  return `rule-${Date.now()}-${Math.random()}`;
};

export const createSupportPolicyDefaultRule = (): SupportPolicyRule => ({
  id: generateRuleId(),
  // @ts-expect-error incorrect library typings does not allow other types than string
  field: SUPPORT_POLICY_CONDITION_FIELDS[0],
  operator: operators[0].name as DataConditionOperator,
  value: [],
});

const EMPTY_RULE_GROUP: SupportPolicyRuleGroup = {
  combinator: 'and',
  rules: [],
};

const DEFAULT_CONDITION_GROUP: ConditionGroup = {
  elementType: 'conditionGroup',
  operator: 'and',
  conditionsElements: [],
};

type SupportPolicyQueryOptions = Partial<Record<SupportedActionQueries, QueryOptionsValue>> &
  UseQueryOptions;

const ENABLED_OPTION_QUERIES = new Set<SupportedActionQueries>([
  SupportedActionQueries.getAddress,
  SupportedActionQueries.getConversationParticipantOptions,
  SupportedActionQueries.getEnabledConversationUrgencies,
  SupportedActionQueries.getAllLabels,
  SupportedActionQueries.getBrand,
  SupportedActionQueries.getServiceLevelOptions,
  SupportedActionQueries.getConversationType,
  SupportedActionQueries.getPartnerGroup,
]);

const DEFAULT_FIELD_MAP = new Map(
  SUPPORT_POLICY_CONDITION_FIELDS.map((field) => [field.attribute, field]),
);

export function useSupportPolicyConditionOptions(): SupportPolicyQueryOptions {
  const { selectedPartner } = useSelectedPartner();

  const baseOptions = useOptionsQueries({
    partnerId: selectedPartner?.partnerId ?? null,
    enabledQueries: ENABLED_OPTION_QUERIES,
    area: 'custom_views',
  });

  return useMemo<SupportPolicyQueryOptions>(() => {
    const participantOptions =
      baseOptions[SupportedActionQueries.getConversationParticipantOptions];
    const filterClientOptions = (
      options?: QueryOptionsValue['options'],
    ): QueryOptionsValue['options'] => {
      if (!options) {
        return [];
      }

      // @ts-expect-error temp
      return options.filter((option: { group?: string; participantEntity?: string }) => {
        const maybeClient = option as { group?: string; participantEntity?: string };
        return maybeClient.group === 'Clients' || maybeClient.participantEntity === 'Client';
      });
    };

    return {
      ...baseOptions,
      [SupportedActionQueries.getConversationParticipantOptions]: {
        ...participantOptions,
        options: filterClientOptions(participantOptions?.options),
        historicalOptions: filterClientOptions(participantOptions?.historicalOptions),
      },
    };
  }, [baseOptions]);
}

export function serializeSupportPolicyConditions(
  rules: SupportPolicyRuleGroup,
  isEnabled: boolean,
): string | null {
  if (!isEnabled) return null;

  const conditionGroup = ruleGroupToConditionGroup(pruneInvalidRules(rules));

  if (!conditionGroup.conditionsElements.length) {
    return null;
  }

  return JSON.stringify(conditionGroup);
}

export function deserializeSupportPolicyConditions(
  raw?: string | null | ConditionGroup,
  fieldMap: Map<string, SupportPolicyConditionField> = DEFAULT_FIELD_MAP,
): SupportPolicyRuleGroup {
  if (!raw) return { ...EMPTY_RULE_GROUP };

  try {
    const parsed = typeof raw === 'string' ? (JSON.parse(raw) as ConditionGroup) : raw;
    return conditionGroupToRuleGroup(parsed, fieldMap);
  } catch (error) {
    console.error('Failed to parse support policy conditions', error);
    return { ...EMPTY_RULE_GROUP };
  }
}

export function mapSupportPolicyRuleValues(
  query: SupportPolicyRuleGroup,
  queryOptions: SupportPolicyQueryOptions,
): SupportPolicyRuleGroup {
  if (!query) {
    return { ...EMPTY_RULE_GROUP };
  }

  const mappedRules = (query.rules ?? []).map((rule) => {
    if ('rules' in rule) {
      return {
        ...rule,
        rules: mapSupportPolicyRuleValues(rule as SupportPolicyRuleGroup, queryOptions).rules,
      };
    }

    const fieldValue =
      typeof rule.field === 'string'
        ? rule.field
        : (rule.field as SupportPolicyConditionField | undefined)?.value;
    const field = SUPPORT_POLICY_CONDITION_FIELDS.find((f) => f.value === fieldValue);

    if (!field?.optionsQuery || !Array.isArray(rule.value)) {
      return rule;
    }

    const optionsData = queryOptions[field.optionsQuery];
    const getOptionValue = (opt?: { value?: unknown; id?: unknown }): string =>
      toPrimitiveStringOrEmpty(opt?.value ?? opt?.id);
    const availableOptions = [
      ...(optionsData?.historicalOptions ?? []),
      ...(optionsData?.options ?? []),
      ...(field.fieldOptions ?? []),
    ];

    const mappedValue = (rule.value as { value?: string | number | boolean }[]).map(
      (valueItem) => {
        const targetValue =
          typeof valueItem === 'object' && valueItem !== null
            ? valueItem.value!
            : (valueItem as unknown as string | number | boolean);

        const targetValueString = toPrimitiveStringOrEmpty(targetValue);
        const matched =
          availableOptions.find((option) => getOptionValue(option) === targetValueString) ||
          valueItem;

        if (typeof matched === 'object') {
          return {
            ...matched,
            value: matched.value ?? targetValue,
            label: (matched as { label?: string }).label ?? targetValueString,
          };
        }

        return {
          value: targetValue,
          label: targetValueString,
        };
      },
    );

    return {
      ...rule,
      value: mappedValue,
    };
  });

  return { ...query, rules: mappedRules as SupportPolicyRuleGroup['rules'] };
}

function ruleGroupToConditionGroup(group?: SupportPolicyRuleGroup): ConditionGroup {
  if (!group?.rules) return { ...DEFAULT_CONDITION_GROUP, conditionsElements: [] };

  return {
    elementType: 'conditionGroup',
    operator: group.combinator || 'and',
    conditionsElements: group.rules
      .map((rule) => mapRuleToCondition(rule))
      .filter(Boolean) as ConditionGroup['conditionsElements'],
  };
}

function mapRuleToCondition(
  rule: SupportPolicyRule | SupportPolicyRuleGroup,
): ConditionElement | null {
  if ('rules' in rule) {
    const nested = ruleGroupToConditionGroup(rule);
    return nested.conditionsElements.length ? nested : null;
  }

  const attribute =
    typeof rule.field === 'string'
      ? rule.field
      : (rule.field as SupportPolicyConditionField | undefined)?.value;

  if (!attribute || isValueEmpty(rule.value)) {
    return null;
  }

  const operator = rule.operator ?? DataConditionOperator.In;
  const normalized = normalizeRuleValues(rule.value);
  if (!normalized.length) {
    return null;
  }

  const value =
    operator === DataConditionOperator.In || operator === DataConditionOperator.NotIn
      ? normalized
      : normalized[0];

  return {
    elementType: 'conditionNode',
    conditionType: ConditionType.Data,
    attribute,
    operator,
    value,
  } as ConditionNode;
}

function conditionGroupToRuleGroup(
  group?: ConditionGroup,
  fieldMap: Map<string, SupportPolicyConditionField> = DEFAULT_FIELD_MAP,
): SupportPolicyRuleGroup {
  if (!group) return { ...EMPTY_RULE_GROUP };

  const rules = (group.conditionsElements ?? [])
    .map((condition, index) => {
      if (condition.elementType === 'conditionGroup') {
        return conditionGroupToRuleGroup(condition, fieldMap);
      }

      if (!isDataConditionNode(condition)) {
        return null;
      }

      const dataCondition = condition;
      const normalizedAttribute =
        dataCondition.attribute === 'brandName'
          ? ('clientSegmentId' as SupportPolicyConditionAttribute)
          : (dataCondition.attribute as SupportPolicyConditionAttribute);

      const customProperty = parseCustomPropertyConditionAttribute(normalizedAttribute);
      const field = fieldMap.get(normalizedAttribute) ?? {
        name: normalizedAttribute,
        value: normalizedAttribute,
        attribute: normalizedAttribute,
        // the field map only holds properties this author can see, so a condition can reference one
        // that is deleted or restricted to another group — keep it editable and say why
        label: customProperty
          ? `Unavailable property (${customProperty.key})`
          : dataCondition.attribute,
        uiType: FormControlType.List,
        controlType: RuleControlType.ConditionControl,
        options: [],
        fieldOptions: [],
        operators,
      };

      const operator = dataCondition.operator ?? DataConditionOperator.In;
      const value = normalizeNodeValue({
        node: dataCondition,
        operator,
        uiType: field.uiType,
        // @ts-expect-error ignore this error temporarily
        field,
        fieldMap,
      });

      return {
        id: `condition-${index}`,
        field,
        operator,
        value,
        valueSource: 'value',
      } as SupportPolicyRule;
    })
    .filter(Boolean) as SupportPolicyRuleGroup[];

  return {
    combinator: group.operator,
    not: false,
    rules,
  };
}

const isRuleGroup = (
  rule: SupportPolicyRuleGroup | SupportPolicyRule,
): rule is SupportPolicyRuleGroup => Array.isArray((rule as SupportPolicyRuleGroup).rules);

function pruneInvalidRules(group?: SupportPolicyRuleGroup): SupportPolicyRuleGroup {
  if (!group?.rules) return { ...EMPTY_RULE_GROUP };

  const cleanedRules = group.rules
    .map((rule) => {
      if (isRuleGroup(rule)) {
        const nested = pruneInvalidRules(rule);
        return nested.rules.length ? nested : null;
      }

      return !rule.field || isValueEmpty(rule.value) ? null : rule;
    })
    .filter(Boolean) as SupportPolicyRule[];

  return {
    ...group,
    rules: cleanedRules,
  };
}

function normalizeRuleValues(value: SupportPolicyRule['value']): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null && 'value' in item) {
          return (item.value as string | number | boolean | undefined) ?? null;
        }
        if (
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean'
        ) {
          return item;
        }
        return null;
      })
      .filter(isPresent)
      .map((item) => toPrimitiveStringOrEmpty(item));
  }

  if (typeof value === 'object' && value !== null && 'value' in value) {
    const val = (value as { value?: unknown }).value;
    return isPresent(val) ? [toPrimitiveStringOrEmpty(val)] : [];
  }

  return isPresent(value) ? [toPrimitiveStringOrEmpty(value)] : [];
}

function normalizeNodeValue({
  node,
  operator,
  uiType,
  field,
  fieldMap,
}: {
  node: ConditionNode;
  operator: DataConditionOperator;
  uiType: FormControlType | undefined;
  field: SupportPolicyConditionField;
  fieldMap: Map<string, SupportPolicyConditionField>;
}): SupportPolicyRule['value'] {
  if (!('value' in node)) return '';

  const values = normalizeRuleValues(node.value as SupportPolicyRule['value']);
  if (!values.length) return '';

  const isMultiValueOperator =
    operator === DataConditionOperator.In || operator === DataConditionOperator.NotIn;

  if (
    uiType === FormControlType.Text ||
    uiType === FormControlType.Number ||
    // a date condition carries one value: an ISO date or a relative label the pickers round-trip
    uiType === FormControlType.Date ||
    uiType === FormControlType.DateTime
  ) {
    return values[0];
  }

  if (uiType === FormControlType.List) {
    const optionSource =
      field.options?.length || field.fieldOptions?.length ? field : fieldMap.get(field.value);
    const optionCandidates = [
      ...(optionSource?.options ?? []),
      ...(optionSource?.fieldOptions ?? []),
    ];

    const mapOption = (value: string): { id: string; value: string; label: string } => {
      const match = optionCandidates.find(
        (option) => String(option.value ?? option.id) === value,
      );
      return {
        id: value,
        value,
        label: (match as { label?: string } | undefined)?.label ?? value,
      };
    };

    if (isMultiValueOperator) {
      return values.map((value) => mapOption(value));
    }

    const [value] = values;
    return mapOption(value);
  }

  if (isMultiValueOperator) {
    return values.map((value) => ({
      id: value,
      value,
      label: toPrimitiveStringOrEmpty(value),
    }));
  }

  return values[0];
}

function isValueEmpty(value: unknown): boolean {
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return isValueEmpty((value as { value?: unknown }).value);
  }
  return typeof value === 'undefined' || value === null;
}

function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function isDataConditionNode(
  condition: ConditionElement,
): condition is Extract<ConditionNode, { conditionType: 'data' }> {
  return (
    condition.elementType === 'conditionNode' &&
    condition.conditionType === ConditionType.Data &&
    'attribute' in condition
  );
}
